import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import Notification from '../Common/Notification';
import { actions } from '../../actions/accountActions';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import ResetPasswordForm from './ResetPasswordForm';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import * as types from '../../constants/actionTypes';
import Routes from '../../constants/routes';

export class ResetPasswordPage extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            isLoading: false,
            isTokenExpired: false,
            password: {
                value: '',
                errors: []
            },
            confirm: {
                value: '',
                errors: []
            }
        };
    }

    async componentDidMount() {
        try {
            this.setState({ isLoading: true });
            const response = await this.props.actions.checkResetToken(this.props.userKey, this.props.token);

            if (response.type === types.CHECK_RESET_TOKEN_SUCCESS) {
                if (response.payload.isTokenExpired) {
                    this.setState({ isTokenExpired: true });
                }
            } else if (response.type === types.CHECK_RESET_TOKEN_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error)
        } finally {
            this.setState({ isLoading: false });
        }
    }

    onChange = (event) => {
        const name = event.currentTarget.name;
        const value = event.currentTarget.value;
        const errors = this.getFieldErrors(name, value);
        this.setState({ [event.currentTarget.name]: { ...this.state[name], value: value, errors: errors } });
    }

    getFieldErrors = (name, value) => {
        let errors = [];
        switch (name) {
            case 'password': {
                if (value.length < 1) {
                    errors.push('Password is required');
                }
                break;
            }
            case 'confirm': {
                const pass = this.state.password.value;
                if (value.length < 1) {
                    errors.push('Password confirmation is required');
                } else if (pass !== value) {
                    errors.push('Passwords do not match');
                }
                break;
            }
            default:
                break;
        }
        return errors;
    }

    isFormValid = () => {
        const passwordErrors = this.getFieldErrors('password', this.state.password.value);
        if (passwordErrors.length > 0) {
            this.setState({ password: { ...this.state.password, errors: passwordErrors } });
        }
        const confirmErrors = this.getFieldErrors('confirm', this.state.confirm.value);
        if (confirmErrors.length > 0) {
            this.setState({ confirm: { ...this.state.confirm, errors: confirmErrors } });
        }
        return passwordErrors.length <= 0 && confirmErrors.length <= 0;
    }

    resetPassword = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!this.isFormValid()) {
            return;
        }

        this.props.actions.resetPassword(this.props.userKey, this.props.token, null, this.state.password.value, this.state.confirm.value)
            .then((result) => {
                if (result.type === types.RESET_PASSWORD_SUCCESS) {
                    Notification("success", "Password Reset Success", "Your password has been successsfully reset");
                    this.props.history.push(Routes.LoginPage);
                } else if (result.type === types.RESET_PASSWORD_FAILURE) {
                    Notification("error", "Error", result.payload.message);
                }
            })
            .catch(error => {
                Notification("error", "Error", error);
            });
    }

    render() {
        return (
            <div className="container">
                <LoadingIndicator isLoading={this.state.isLoading}>
                    {
                        this.state.isTokenExpired ?
                            <div>
                                <div className="alert alert-danger" role="alert">Your password reset link has expired.</div>
                                <p>
                                    Please try the <a href={Routes.ForgotPassword}>Forgot Password Process</a> again to send yourself another password reset link.
                                    For security purposes, the password reset links expire after 15 minutes.
                                </p>
                            </div>
                            :
                            <ResetPasswordForm
                                title="Create Password"
                                password={this.state.password}
                                confirm={this.state.confirm}
                                onSubmit={this.resetPassword}
                                onChange={this.onChange}
                                isTokenExpired={this.state.isTokenExpired}
                            />
                    }
                </LoadingIndicator>
            </div>
        );
    }

}

ResetPasswordPage.propTypes = {
    userKey: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
    const urlParams = new URLSearchParams(ownProps.location.search);
    let userKey = '', token = '';
    if (urlParams.has('id')) {
        userKey = urlParams.get('id');
    }

    if (urlParams.has('token')) {
        token = urlParams.get('token');
    }

    return {
        userKey: userKey,
        token: token,
        history: ownProps.history
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({ ...actions }, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ResetPasswordPage);